/* NotificationCenter.jsx — Premium operational notification surface.
 *
 * Dropdown rendered from the topbar bell. Reads from `chainStore`
 * (which fetches `/api/notifications`) and groups by priority +
 * category. Tapping a row marks it read and deep-links into operational
 * context (Deal Room / Documents / Participants / Messages section).
 *
 * Visual identity: ChainFlow OS glassmorphism, restrained tone, NO
 * aggressive red, NO gamification, NO "Urgent" stickers. Priority is
 * communicated through quiet violet/amber/slate accent — never alarm.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertOctagon,
  Bell,
  Calendar,
  CheckCheck,
  FileText,
  GitBranch,
  Inbox,
  Info,
  Mail,
  ClipboardCheck,
  Sparkles,
  Users,
  Clock,
  X,
} from "lucide-react";
import { useChainStore, cfBus } from "../../data/chainStore";
import { PushEnablementRow } from "./PushEnablementRow";
import { SignalIntelligencePanel } from "./SignalIntelligencePanel";

/* ─── Icon registry — keyed by backend `icon_key` ──────────────────── */
const ICONS = {
  file:      FileText,
  milestone: GitBranch,
  users:     Users,
  mortgage:  Sparkles,
  chain:     GitBranch,
  alert:     AlertOctagon,
  calendar:  Calendar,
  clock:     Clock,
  task:      ClipboardCheck,
  message:   Mail,
  info:      Info,
};

const PRIORITY_ORDER = { critical: 0, important: 1, informational: 2 };

/* Calm tone tokens — no aggressive red. Critical = subtle rose-tinted
   border + violet-deep icon. Important = amber-soft. Informational =
   slate-soft. Visual hierarchy is communicated through depth + weight,
   never through alarm. */
const TONE = {
  critical: {
    iconBg: "var(--gp-rose-soft)",
    iconColor: "#B91C1C",
    iconBorder: "1px solid var(--gp-rose-border)",
    accentBar: "linear-gradient(180deg, #B91C1C 0%, rgba(185,28,28,0.4) 100%)",
    rowTint: "rgba(254,242,242,0.55)",
  },
  important: {
    iconBg: "var(--gp-amber-soft)",
    iconColor: "#B45309",
    iconBorder: "1px solid var(--gp-amber-border)",
    accentBar: "linear-gradient(180deg, #D97706 0%, rgba(217,119,6,0.35) 100%)",
    rowTint: "transparent",
  },
  informational: {
    iconBg: "var(--gp-violet-soft)",
    iconColor: "var(--gp-violet-deep)",
    iconBorder: "1px solid var(--gp-violet-border)",
    accentBar: "linear-gradient(180deg, var(--gp-violet) 0%, rgba(124,58,237,0.20) 100%)",
    rowTint: "transparent",
  },
};

const fmtAgo = (ts) => {
  const m = Math.floor((Date.now() - ts) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const tonePill = (priority) => {
  switch (priority) {
    case "critical":
      return { label: "Critical", cls: "gp-pill gp-pill-rose" };
    case "important":
      return { label: "Action", cls: "gp-pill gp-pill-amber" };
    default:
      return null;   // Informational has no pill — only the violet accent bar
  }
};

/* ─── Row ──────────────────────────────────────────────────────────── */
const NotificationRow = ({ n, onOpen }) => {
  const Icon = ICONS[n.iconKey || "info"] || Info;
  const tone = TONE[n.priority] || TONE.informational;
  const pill = tonePill(n.priority);
  return (
    <li
      onClick={() => onOpen(n)}
      className="relative flex items-start gap-3 px-5 py-3.5 cursor-pointer transition-colors group"
      style={{
        background: !n.read ? tone.rowTint : "transparent",
        borderBottom: "1px solid var(--gp-card-border)",
      }}
      data-testid={`notif-${n.id}`}
    >
      {/* Priority accent bar on the left */}
      {!n.read && (
        <span
          aria-hidden="true"
          className="absolute left-0 top-2.5 bottom-2.5 w-[3px] rounded-full"
          style={{ background: tone.accentBar }}
        />
      )}
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{
          background: tone.iconBg,
          color: tone.iconColor,
          border: tone.iconBorder,
        }}
      >
        <Icon size={14} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 flex-wrap">
          {n.title ? (
            <p
              className={`text-[12.5px] leading-snug flex-1 ${!n.read ? "font-bold" : "font-semibold"}`}
              style={{ color: "var(--gp-text)" }}
            >
              {n.title}
            </p>
          ) : (
            <p
              className={`text-[12.5px] leading-snug flex-1 ${!n.read ? "font-bold" : ""}`}
              style={{ color: !n.read ? "var(--gp-text)" : "var(--gp-text-muted)" }}
            >
              {n.body}
            </p>
          )}
          {pill ? <span className={pill.cls}>{pill.label}</span> : null}
        </div>
        {n.title && n.body ? (
          <p
            className="text-[11.5px] mt-0.5 leading-snug line-clamp-2"
            style={{ color: "var(--gp-text-muted)" }}
          >
            {n.body}
          </p>
        ) : null}
        <p
          className="text-[10.5px] mt-1.5 tabular-nums"
          style={{ color: "var(--gp-text-subtle)" }}
        >
          {fmtAgo(n.at)}
        </p>
      </div>
    </li>
  );
};

/* ─── Group header ─────────────────────────────────────────────────── */
const GroupHeader = ({ label, count }) => (
  <div
    className="sticky top-0 z-10 px-5 py-1.5 flex items-center justify-between"
    style={{
      background: "rgba(255,255,255,0.94)",
      backdropFilter: "blur(8px)",
      borderBottom: "1px solid var(--gp-card-border)",
    }}
  >
    <span
      className="text-[10px] uppercase tracking-[0.16em] font-bold"
      style={{ color: "var(--gp-text-subtle)" }}
    >
      {label}
    </span>
    <span
      className="text-[10.5px] font-semibold tabular-nums"
      style={{ color: "var(--gp-text-subtle)" }}
    >
      {count}
    </span>
  </div>
);

/* ─── Main center ──────────────────────────────────────────────────── */
export const NotificationCenter = () => {
  const { notifications, markNotifRead, markAllNotifRead } = useChainStore();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => cfBus.on("cf:open-notifications", () => setOpen((v) => !v)), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const unread = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications],
  );

  /* Intelligent grouping: priority first (Critical → Important →
     Informational), then time-desc within each band. This is the calm
     digest the user scans top-down. */
  const groups = useMemo(() => {
    const sorted = [...notifications].sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority ?? "informational"] ?? 2;
      const pb = PRIORITY_ORDER[b.priority ?? "informational"] ?? 2;
      if (pa !== pb) return pa - pb;
      return b.at - a.at;
    });
    const out = { critical: [], important: [], informational: [] };
    for (const n of sorted) {
      const bucket = out[n.priority] ? n.priority : "informational";
      out[bucket].push(n);
    }
    return out;
  }, [notifications]);

  const onOpen = (n) => {
    markNotifRead(n.id);
    const dest = n.deepLink || (n.dealId ? `/deal-rooms/${n.dealId}` : null);
    if (dest) {
      nav(dest);
      setOpen(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80]"
      data-testid="notification-center"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="absolute top-16 right-4 sm:right-6 w-[440px] max-w-[94vw] max-h-[78vh] rounded-3xl flex flex-col overflow-hidden cf-fade-up"
        style={{
          background: "rgba(255,255,255,0.98)",
          backdropFilter: "blur(20px) saturate(150%)",
          border: "1px solid var(--gp-card-border)",
          boxShadow:
            "0 24px 60px -16px rgba(15,23,42,0.18), 0 4px 14px -4px rgba(15,23,42,0.08)",
        }}
      >
        <header
          className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid var(--gp-card-border)" }}
        >
          <div>
            <div className="gp-eyebrow flex items-center gap-2 mb-0.5">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full cf-pulse"
                style={{ background: "var(--gp-violet)" }}
              />
              OPERATIONAL SIGNALS
            </div>
            <h3
              className="text-[15px] font-bold tracking-tight"
              style={{ color: "var(--gp-text)" }}
              data-testid="notif-center-title"
            >
              Notifications
              {unread > 0 ? (
                <span
                  className="ml-2 text-[12px] font-bold tabular-nums"
                  style={{ color: "var(--gp-violet-deep)" }}
                  data-testid="notif-center-unread-count"
                >
                  · {unread} new
                </span>
              ) : null}
            </h3>
          </div>
          <div className="flex items-center gap-1">
            {unread > 0 ? (
              <button
                onClick={markAllNotifRead}
                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg transition-colors hover:bg-violet-50"
                style={{ color: "var(--gp-violet-deep)" }}
                data-testid="notif-mark-all"
              >
                <CheckCheck size={11} /> Mark all
              </button>
            ) : null}
            <button
              onClick={() => setOpen(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100"
              style={{ color: "var(--gp-text-subtle)" }}
              aria-label="Close"
              data-testid="notif-close"
            >
              <X size={14} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto" style={{ background: "var(--gp-surface-soft, #FAFAFC)" }}>
          <PushEnablementRow />
          <SignalIntelligencePanel onClose={() => setOpen(false)} />
          {notifications.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              {groups.critical.length > 0 ? (
                <>
                  <GroupHeader label="Needs attention" count={groups.critical.length} />
                  <ul data-testid="notif-group-critical">
                    {groups.critical.map((n) => (
                      <NotificationRow key={n.id} n={n} onOpen={onOpen} />
                    ))}
                  </ul>
                </>
              ) : null}
              {groups.important.length > 0 ? (
                <>
                  <GroupHeader label="Important" count={groups.important.length} />
                  <ul data-testid="notif-group-important">
                    {groups.important.map((n) => (
                      <NotificationRow key={n.id} n={n} onOpen={onOpen} />
                    ))}
                  </ul>
                </>
              ) : null}
              {groups.informational.length > 0 ? (
                <>
                  <GroupHeader label="Informational" count={groups.informational.length} />
                  <ul data-testid="notif-group-informational">
                    {groups.informational.map((n) => (
                      <NotificationRow key={n.id} n={n} onOpen={onOpen} />
                    ))}
                  </ul>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ─── Empty state ──────────────────────────────────────────────────── */
const EmptyState = () => (
  <div className="p-10 text-center" data-testid="notif-empty">
    <div
      className="w-14 h-14 mx-auto rounded-2xl flex items-center justify-center mb-3"
      style={{
        background: "var(--gp-green-soft)",
        border: "1px solid var(--gp-green-border)",
        color: "#047857",
      }}
    >
      <Inbox size={20} />
    </div>
    <p
      className="text-[14px] font-bold"
      style={{ color: "var(--gp-text)" }}
    >
      Inbox quiet
    </p>
    <p
      className="text-[12px] mt-1"
      style={{ color: "var(--gp-text-muted)" }}
    >
      No operational signals right now — every deal is moving.
    </p>
  </div>
);

/* ─── Topbar bell button ───────────────────────────────────────────── */
export const NotificationButton = () => {
  const { notifications } = useChainStore();
  const unread = notifications.filter((n) => !n.read).length;
  return (
    <button
      onClick={() => cfBus.emit("cf:open-notifications")}
      className="relative w-9 h-9 rounded-xl bg-white border border-slate-200 hover:border-violet-300 text-slate-600 hover:text-violet-700 flex items-center justify-center transition-all"
      data-testid="topbar-notif-btn"
      aria-label="Notifications"
    >
      <Bell size={14} />
      {unread > 0 ? (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[9.5px] font-bold flex items-center justify-center tabular-nums"
          style={{
            background:
              "linear-gradient(135deg, var(--gp-violet-deep) 0%, #4F46E5 100%)",
            boxShadow: "0 4px 10px -2px rgba(99,102,241,0.55)",
          }}
        >
          {unread > 9 ? "9+" : unread}
        </span>
      ) : null}
    </button>
  );
};
